/* eslint-disable react-hooks/exhaustive-deps */
import { cloneDeep, isEmpty, isEqual, set, concat, orderBy } from 'lodash';
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { currencyCodes, convertSelectLabel, checkArray, supplierChangeKeysArray, recursiveSearch } from '../../../../Components/helper';

import {
  bomProductTesting,
  fetchExchangeRateArr,
  fetchProductColourways,
  fetchProductLengths,
  fetchProductSizes,
  getProductCostingDetailsById,
  getProductCostingTemplateDetailsByIds,
  getUnIntegratedSupplierColouways,
  getUnIntegratedSupplierCostingDetailsById,
  getUnIntegratedSupplierUnits,
  setPreviousProductCostingData,
} from '../store/productCosting.actions';
import { getProductCostingSampleRefs } from '../../../../services/product-costing.service';
import { fetchSupplier, fetchProductDetails, fetchConfig } from '../../Bom/store/BOMConfig.action';
import { getTestingLibraries } from '../../../SupplierSuggestion/store/SupplierSuggestion.action';
import {
  getAllUnitDetails,
  getActiveIncotermsDetails,
  getLicenseDetails,
  getLikenessDetails,
  getSubBrandDetails,
} from '../../../SystemMasters/Store/SystemMasters.action';
import { getCostingTemplateListByDevOrder } from '../../../Library/CostingTemplateTSX/store/costingTemplate.actions';
import { fetchSuppliersFactories } from '../../../Library/BomLibrary/store/BomLibrary.action';
import { commonPath, landedInfoFieldPath } from '../fieldPath';
import { mergeTemplateRequirements } from '../specification-requirements.helpers';
import useValidationHooks from '../Hooks/useValidationHooks';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import type {
  AdditionalComponentState,
  SpecificationRequirementState,
  SpecificationRequirementRow,
  AdditionalState,
  BomTabWithSequence,
  ColSizeLengthToggleState,
  ColourwayOption,
  CostingRootState,
  DefaultTabState,
  DetailedCostingState,
  DetailedQuantitiesState,
  DistributionCentre,
  ExpandAllState,
  GrandTotalState,
  InitialOptionsState,
  InternalState,
  LandedInfoState,
  LandedInfoStateEntry,
  ProductCostingContextValue,
  ProductCostingState,
  SelectOption,
  TestingState,
} from '../types';
import type { CostingInfoField, CostingTemplateDetail, ProductCostingDetail } from '../store/productCosting.types';
import { fetchAutomatedFreight } from '../store/productCosting.actions';
import type { AutomatedFreightDto } from '../../../../services/automated-freight.service';
import type { ProductCostingDispatch } from '../store/productCosting.types';

export const StateContext = createContext<ProductCostingContextValue | null>(null);

/**
 * Provider for the Product Costing detail page.
 *
 * Holds the page's 17 pieces of state, loads every master list the tables depend on, and hydrates
 * itself from the costing document once redux has it. Ported 1:1 from
 * NewProductCosting/ProductCostingContext/index.js — the plain `useState` shape is deliberate and
 * is not converted to a reducer.
 */
export default function NewProductCostingDetailsPage({ children }: { children?: React.ReactNode }): React.JSX.Element {
  const { devOrderId, productid } = useParams<{ devOrderId?: string; productid?: string }>();
  const { search, pathname } = useLocation();
  const queryParams = new URLSearchParams(search);
  const queryDevOrderId = queryParams.get('devOrderId');
  const queryCostingId = queryParams.get('costingId');
  const querySupplierId = queryParams.get('supplierId');
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const isGlobalUrl = Boolean(querySupplierId && queryDevOrderId && queryCostingId && pathname.includes('product-costing-supplier'));

  /**
   * The non-integrated supplier reaches this page through a query-string link, so
   * `useParams()` yields nothing. Every id-gated effect below must use these
   * instead of the raw route params or it never runs on that route.
   */
  const effectiveCostingId = productid || queryCostingId;
  const effectiveDevOrderId = devOrderId || queryDevOrderId;

  const {
    Users: { logInRights, profileDetails },
    ColorLibraryDetails: { ColorLibList },
    newProductCostingData: { productColours, productSizes, productUnitList, productLengths, productCostingDetail },
    SystemMastersDetails: { unitList },
    UserDetails: { isSupplierLoggedIn, userInfo },
    BOMConfigDetails: { tabs, productDetails },
  } = useSelector((state: CostingRootState) => state);
  /* UserRights.js declares `CheckActionPermission(rights, section, module = null, action = null)`,
     so TypeScript infers the last two parameters as `null` from their defaults. The cast restores
     the signature the function actually accepts. */
  const { CheckActionPermission } = useComponent() as {
    CheckActionPermission: (rights: unknown, section: string, module?: string | null, action?: string | null) => boolean;
  };
  const CoastingAddEditRights: boolean = CheckActionPermission(logInRights, 'products', 'costing', 'addEdit');
  const CoastingDeleteRights: boolean = CheckActionPermission(logInRights, 'products', 'costing', 'delete');

  /* The seven `initial*` values below are published on the context and listed in its useMemo
     dependencies. In the original they were plain object literals, rebuilt on every render, so
     their identity always changed and the memo never held — every consumer (all nine grids and the
     whole detail page) re-rendered on every render of this provider. That was B1.

     useMemo with an empty dependency list gives each MOUNT its own stable instance. Module-level
     constants would be stabler still, but they would be shared across mounts, and a stray in-place
     mutation would then outlive the page. Per-mount isolation matches the original's semantics. */
  const initialSetting = useMemo<ProductCostingState>(() => ({
    productId: null,
    isGlobalUrl: false,
    productInfo: {
      supplierId: null,
      currency: null,
      incoterms: null,
      reference: '',
      shipmentMethod: null,
      factoryId: null,
      moqNeeded: '',
      targetCost: '',
    },
    averageCosting: {
      averageCostingInfo: [],
      sumOfTotalQuantity: 0,
      totalPricePerUnit: 0,
      averageCostingActive: true,
      overAllSumOfQuantityPerSizCol: 0,
      overAllSumOfTotalPrice: 0,
      overAllSumOfPricePerUnit: 0,
      loader: false,
      totLength: [],
      totSize: [],
      totColourways: [],
    },
    currencyOptionsArr: currencyCodes.map((currKey: string) => convertSelectLabel(currKey, true)) as SelectOption[],
    status: 'Draft',
    isBreakdown: null,
    isBrekdownManualToggle: null,
    productTemplate: null,
    sampleRef: null,
    costingSizes: [],
    isAccessDenied: false,
    landedInformationColumns: [],
  }), []);

  const initialTestingState = useMemo<TestingState>(() => ({
    testing: {
      testingInfo: [],
      sumOfPrice: '',
    },
  }), []);

  const initialAdditionalState = useMemo<AdditionalState>(() => ({
    additional: {
      additionalInfo: [],
    },
  }), []);

  /* Not published on the context, so it only seeds useState and needs no memo. */
  const initialAdditionalComponentState: AdditionalComponentState = {
    additionalComponent: {
      additionalComponentInfo: [],
    },
  };

  /* Not published on the context either, for the same reason — seeds useState only. */
  const initialSpecificationRequirementState: SpecificationRequirementState = {
    specificationRequirement: {
      specificationRequirementInfo: [],
    },
  };

  const grandTotalObj = useMemo<GrandTotalState>(() => ({
    grandTotal: [],
    totalBomTypeTotalPrice: '0',
    totalOverAllSumTotalPrice: '0',
  }), []);

  const initialInternalState: InternalState = {
    costingComment: '',
    markAsComplete: false,
    isChangedBySupllierState: false,
    activeTab: 'product-costing',
    commentMsg: '',
    productCostCommentModal: false,
    inviteBtn: false,
    primaryCurrValue: 1,
    secondaryCurrValue: 0,
    currSelectedTemplateDetails: null,
    hideColourCol: false,
    hideSizeCol: false,
    hideLengthCol: false,
    costingInfoValues: {},
  };

  const initialOptionsSetting: InitialOptionsState = {
    sizeOptionsArr: [],
    uomOptionsArr: [],
    colourRefOptionsArr: [],
    colourwayOptionsArr: [],
    sampleRefOptionsArr: [],
  };

  const initialCostingCombinationBase: ColSizeLengthToggleState = {
    colourWayToggle: true,
    sizeLengthToggle: false,
  };

  const initialLandedInfoState = useMemo<LandedInfoState>(() => ({
    landedInfo: {
      landedInfoColumnArr: [],
      landedInfoArr: [],
      currency: null,
      targetCost: '',
    },
  }), []);

  const [defaultTab, setDefaultTabs] = useState<DefaultTabState>({
    moduleActiveTab: 'productCosting',
    subModuleActiveTab: 'averageCosting',
  });

  const initialDetailedCostingState = useMemo<DetailedCostingState>(() => ({
    detailedCosting: {
      bomTypeArr: [],
      detailedCostingActive: false,
    },
  }), []);

  const initialDetailedCostingQuanitiesState = useMemo<DetailedQuantitiesState>(() => ({
    quantities: {
      quantitiesInfo: [],
      sumOfTotalQuantity: 0,
      totalPricePerUnit: 0,
      sumOfQuantityPerSizCol: 0,
      overAllSumOfQuantityPerSizCol: 0,
      totLength: [],
      totSize: [],
      totColourways: [],
    },
  }), []);

  const initialExpandAllState: ExpandAllState = {
    averageCostingAllExpand: false,
    quantitesAllExpand: false,
    bomAllExpand: false,
    grandTotalAllExpand: false,
  };

  const [productCostingstate, setProductCostingState] = useState<ProductCostingState>(initialSetting);
  const [testingState, setTestingState] = useState<TestingState>(initialTestingState);
  const [additionalState, setAdditionalState] = useState<AdditionalState>(initialAdditionalState);
  const [grandTotalState, setGrandTotalState] = useState<GrandTotalState>(grandTotalObj);
  const [internalState, setInternalState] = useState<InternalState>(initialInternalState);
  const [initialOptionsState, setInitialOptionValues] = useState<InitialOptionsState>(initialOptionsSetting);
  const [colSizeLengthToggleState, setActivateColSizeLengthToggle] = useState<ColSizeLengthToggleState>(initialCostingCombinationBase);
  const [landedInfoState, setLandedInfoState] = useState<LandedInfoState>(initialLandedInfoState);
  const [landedInfoStates, setLandedInfoStates] = useState<LandedInfoStateEntry[]>([]);
  const [detailedCostingState, setDetailedCostingState] = useState<DetailedCostingState>(initialDetailedCostingState);
  const [detailedQuanitiesState, setDetailedQuanitiesState] = useState<DetailedQuantitiesState>(initialDetailedCostingQuanitiesState);
  const [loader, setCostingLoader] = useState<boolean>(false);
  const [commentLoader, setCostingCommentLoader] = useState<boolean>(false);
  const [isAllExpandedState, setExpandAllState] = useState<ExpandAllState>(initialExpandAllState);
  const [additionalComponentState, setAdditionalComponentState] = useState<AdditionalComponentState>(initialAdditionalComponentState);
  const [specificationRequirementState, setSpecificationRequirementState] = useState<SpecificationRequirementState>(initialSpecificationRequirementState);
  const [reRenderLandedInfo, setReRenderLandedInfo] = useState<boolean>(false);

  /**
   * The distribution centres this product's brand or sub-brand is served by. A sub-brand match wins
   * outright; only when the sub-brand matches nothing does the brand-level list apply.
   */
  const filteredDistributionCentres = useMemo<DistributionCentre[]>(() => {
    const allDCs = profileDetails?.distributionCentres || [];
    const brandId = (productDetails?.brandId as { _id?: string } | undefined)?._id || (productDetails?.brandId as string | undefined);
    const subBrandId = (productDetails?.subBrandId as { _id?: string } | undefined)?._id || (productDetails?.subBrandId as string | undefined);
    if (!allDCs.length || (!brandId && !subBrandId)) return [];
    const matchId = (arr: unknown[] | undefined, id: string | undefined): boolean =>
      Boolean(arr?.some((x) => (x as string)?.toString() === id?.toString() || (x as { _id?: string })?._id?.toString() === id?.toString()));
    if (subBrandId) {
      const bySubBrand = allDCs.filter((dc) => matchId(dc.subBrands, subBrandId));
      if (bySubBrand.length) return bySubBrand;
    }
    return allDCs.filter((dc) => matchId(dc.brands, brandId));
  }, [profileDetails?.distributionCentres, productDetails?.brandId, productDetails?.subBrandId]);

  useEffect(() => {
    if (!productid && filteredDistributionCentres.length > 0) {
      setLandedInfoStates(
        filteredDistributionCentres.map((dc) => ({
          distributionCentre: dc,
          landedInfo: { landedInfoColumnArr: [], landedInfoArr: [], currency: null, targetCost: '' },
          landedExchangeRateInformation: {},
        })),
      );
    }
  }, [filteredDistributionCentres.length, productid]);

  useEffect(() => {
    const callFn = async () => {
      if (effectiveCostingId && productCostingDetail && effectiveDevOrderId) {
        setProductCostingState((state) => ({
          ...state,
          productInfo: {
            supplierId: productCostingDetail?.productInfo?.supplierId,
            currency: productCostingDetail?.productInfo?.currency,
            incoterms: productCostingDetail?.productInfo?.incoterms,
            reference: productCostingDetail?.productInfo?.reference,
            shipmentMethod: productCostingDetail?.productInfo?.shipmentMethod,
            factoryId: productCostingDetail?.productInfo?.factoryId,
            moqNeeded: productCostingDetail?.productInfo?.moqNeeded,
            targetCost: productCostingDetail?.productInfo?.targetCost,
          } as ProductCostingState['productInfo'],
          averageCosting: {
            ...productCostingDetail?.averageCosting,
            averageCostingActive: productCostingDetail?.isAverageCosting,
          } as ProductCostingState['averageCosting'],
          productTemplate: productCostingDetail?.productTemplate ?? null,
          sampleRef: productCostingDetail?.sampleRef,
          status: productCostingDetail?.status as string,
          costingSizes: productCostingDetail?.sizes as string[],
          landedInformationColumns: productCostingDetail?.productTemplate?.landedInfo as unknown[],
        }));
        setDefaultTabs((state) => ({
          ...state,
          subModuleActiveTab: productCostingDetail?.isAverageCosting ? 'averageCosting' : 'detailedCosting',
        }));
        setInternalState((state) => ({
          ...state,
          markAsComplete: Boolean(productCostingDetail?.markAsComplete),
          costingInfoValues: (productCostingDetail?.productTemplate?.costingInfo || [])
            .filter((ci: CostingInfoField) => !ci?.isDefault && ci?.value !== undefined && ci?.value !== null)
            .reduce<Record<string, unknown>>((acc, ci: CostingInfoField) => {
              acc[ci._id] = ci?.type === 'datePicker' && ci?.value ? new Date(ci.value as string) : ci.value;
              return acc;
            }, {}),
        }));
        setActivateColSizeLengthToggle((state) => ({
          ...state,
          colourWayToggle: productCostingDetail?.colourWayToggle as boolean,
          sizeLengthToggle: productCostingDetail?.sizeLengthToggle as boolean,
        }));
        setTestingState((state) => ({
          ...state,
          testing: productCostingDetail?.testing as TestingState['testing'],
        }));
        setAdditionalState((state) => ({
          ...state,
          additional: productCostingDetail?.additional as AdditionalState['additional'],
        }));
        setDetailedQuanitiesState((state) => ({
          ...state,
          quantities: { ...state.quantities, ...productCostingDetail?.quantities } as DetailedQuantitiesState['quantities'],
        }));
        setDetailedCostingState((state) => ({
          ...state,
          detailedCosting: {
            ...state?.detailedCosting,
            ...productCostingDetail?.detailedCosting,
            detailedCostingActive: productCostingDetail?.isDetailedCosting,
          } as DetailedCostingState['detailedCosting'],
        }));
        setAdditionalComponentState((state) => ({
          ...state,
          additionalComponent: { ...state.additionalComponent, ...productCostingDetail?.additionalComponent } as AdditionalComponentState['additionalComponent'],
        }));
        /*
         * Seeds the Spec Requirements rows from the costing's own template snapshot when the costing
         * has never saved any. Without this a costing only ever got its rows from the template-select,
         * costing-type-switch or Refresh paths, so opening one that already had a template — every
         * Lambda-created costing — showed an empty section.
         *
         * Saved answers win outright: rebuilding against the template here would resurrect rows the
         * user deleted, and deletion is only supposed to be undone by an explicit Refresh.
         */
        /*
         * Saved answers only. The seed does NOT come from here: the embedded snapshot deliberately
         * carries just the flag and heading, so the requirement LIST is read live in
         * `handleLandedInformationChanges` below — the same split `additionalComponentInfo` uses.
         */
        setSpecificationRequirementState((state) => ({
          ...state,
          specificationRequirement: {
            ...state.specificationRequirement,
            specificationRequirementInfo: Array.isArray(
              (productCostingDetail?.specificationRequirement as { specificationRequirementInfo?: SpecificationRequirementRow[] } | undefined)
                ?.specificationRequirementInfo,
            )
              ? ((productCostingDetail?.specificationRequirement as { specificationRequirementInfo?: SpecificationRequirementRow[] })
                  .specificationRequirementInfo as SpecificationRequirementRow[])
              : [],
          },
        }));
        /**
         * Landed Information is hidden from every supplier view, and the template
         * lookup it performs is not JWT-excluded, so skip it on the credential-less
         * route rather than firing a call that can only 401.
         */
        if (!isGlobalUrl) {
          handleLandedInformationChanges(productCostingDetail);
        }
        dispatch(setPreviousProductCostingData(productCostingDetail));
      }
    };
    if (effectiveCostingId && effectiveDevOrderId && productCostingDetail) {
      callFn();
    }
  }, [effectiveCostingId, productCostingDetail, effectiveDevOrderId, isGlobalUrl]);

  /**
   * Rebuilds the per-distribution-centre landed blocks from the saved document, falling back to the
   * single legacy `landedInformation` block for costings saved before multi-DC support.
   */
  const handleLandedInformationChanges = async (costingDetail: ProductCostingDetail): Promise<void> => {
    const res = await dispatch(
      getProductCostingTemplateDetailsByIds(costingDetail?.productTemplate?._id, devOrderId, {
        colourWayToggle: costingDetail?.colourWayToggle,
        sizeLengthToggle: costingDetail?.sizeLengthToggle,
      }) as never,
    );
    if (res) {
      setInternalState((state) => ({
        ...state,
        currSelectedTemplateDetails: res as unknown as InternalState['currSelectedTemplateDetails'],
      }));

      /*
       * Seeds Spec Requirements from the LIVE template, and only for a costing that has never saved
       * the section. An empty saved array is a deliberate "the user deleted them all" and must
       * survive a reload, so presence of the key — not its length — is the test.
       */
      const savedRequirementRows = (
        costingDetail?.specificationRequirement as { specificationRequirementInfo?: SpecificationRequirementRow[] } | undefined
      )?.specificationRequirementInfo;
      if (!Array.isArray(savedRequirementRows)) {
        setSpecificationRequirementState((state) => ({
          ...state,
          specificationRequirement: {
            ...state.specificationRequirement,
            specificationRequirementInfo: mergeTemplateRequirements(res as unknown as CostingTemplateDetail, []),
          },
        }));
      }

      const storedEntries = costingDetail?.landedInformations || [];
      if (storedEntries.length > 0) {
        const rebuilt: LandedInfoStateEntry[] = storedEntries.map((entry) => ({
          distributionCentre: {
            distributionName: entry.distributionCentreName,
            currency: entry.distributionCentreCurrency,
          },
          landedInfo: {
            landedInfoColumnArr: [],
            landedInfoArr: (entry.landedInfoArr || []) as LandedInfoState['landedInfo']['landedInfoArr'],
            currency: null,
            targetCost: '',
          },
          landedExchangeRateInformation: entry.landedExchangeRateInformation || {},
        }));
        setLandedInfoStates(rebuilt);
        const hasData = storedEntries.some((e) => (e.landedInfoArr?.length ?? 0) > 0);
        if (hasData) {
          setReRenderLandedInfo(true);
        }
      } else {
        setLandedInfoState((state) => ({
          ...state,
          landedInfo: { ...state.landedInfo, landedInfoArr: costingDetail?.landedInformation?.landedInfoArr as LandedInfoState['landedInfo']['landedInfoArr'] },
        }));
        if ((costingDetail?.landedInformation?.landedInfoArr?.length ?? 0) > 0) {
          setReRenderLandedInfo(true);
        }
      }
    }
  };

  /**
   * Incoterms master list feeds the internal dropdown only. Every supplier view
   * renders the saved incoterm as static text, and this endpoint is not
   * JWT-excluded, so skip it on the credential-less route.
   */
  useEffect(() => {
    if (!isGlobalUrl) {
      dispatch(getActiveIncotermsDetails({ isActive: true }));
    }
  }, [isGlobalUrl]);

  useEffect(() => {
    if (productDetails?.likenessId && productDetails?.licensorId) {
      dispatch(
        getLikenessDetails({
          licensorId: productDetails?.licensorId,
          licenseId: productDetails?.licenseId,
          loggedInUserId: userInfo?.userId,
          isActive: true,
        }),
      );
    }
    if (productDetails?.licenseId && productDetails?.licensorId) {
      dispatch(
        getLicenseDetails({
          licensorId: productDetails?.licensorId,
          loggedInUserId: userInfo?.userId,
          isActive: true,
        }),
      );
    }
  }, [productDetails?.likenessId, productDetails?.licenseId, productDetails?.licensorId]);

  useEffect(() => {
    if (!isEmpty(productDetails) && !isEmpty(productDetails?.seasonId) && !isEmpty(productDetails?.year)) {
      const obj = {
        seasonId: productDetails?.seasonId,
        year: productDetails?.year,
      };
      dispatch(fetchExchangeRateArr(obj) as never);
    }
  }, [dispatch, productDetails]);

  useEffect(() => {
    const brandId = (productDetails?.brandId as { _id?: string } | undefined)?._id || (productDetails?.brandId as string | undefined);
    if (brandId) {
      dispatch(
        getSubBrandDetails({
          brandId,
          loggedInUserId: userInfo?.userId,
          isActive: true,
        }),
      );
    }
  }, [dispatch, productDetails?.brandId]);

  useEffect(() => {
    const callFn = async () => {
      setCostingLoader(true);
      const res = await dispatch(getProductCostingDetailsById(productid, t) as never);
      if ((res as unknown as number) === 500) {
        setProductCostingState((state) => ({
          ...state,
          isAccessDenied: true,
        }));
      }
      setCostingLoader(false);
    };
    if (!isEmpty(productid) && !isGlobalUrl) callFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, productid, isGlobalUrl]);

  /**
   * Bootstrap for the non-integrated supplier link. Every other fetch effect in
   * this provider is gated `!isGlobalUrl` because it targets authenticated
   * dev-order endpoints; these four are the JWT-excluded costing equivalents and
   * are the only calls this route is allowed to make.
   */
  useEffect(() => {
    const callFn = async () => {
      setCostingLoader(true);
      const obj = {
        productId: queryCostingId,
        devOrderId: queryDevOrderId,
        supplierId: querySupplierId,
      };
      await dispatch(getUnIntegratedSupplierColouways(obj) as never);
      await dispatch(getUnIntegratedSupplierUnits(obj) as never);
      const res = (await dispatch(getUnIntegratedSupplierCostingDetailsById(obj) as never)) as unknown;
      if (res === 400 || res === 500 || res === false) {
        setProductCostingState((state) => ({
          ...state,
          isAccessDenied: true,
        }));
      }
      setCostingLoader(false);
    };
    if (isGlobalUrl) callFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isGlobalUrl]);

  useEffect(() => {
    setInternalState((state) => ({
      ...state,
      hideColourCol: productColours?.length > 0,
      hideSizeCol: productSizes?.length > 0,
      hideLengthCol: productLengths?.length > 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productColours?.length, productSizes?.length, productLengths?.length, productid]);

  useEffect(() => {
    const callFn = () => {
      const copyState = cloneDeep(productCostingstate);
      set(copyState, commonPath().PRODUCT_ID, productid || queryCostingId);
      setProductCostingState((state) => ({
        ...state,
        ...copyState,
      }));
    };
    if (!isEmpty(productid) || !isEmpty(queryCostingId)) callFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productid, queryCostingId]);

  const { checkValidation, setGlobalErrors, globalError } = useValidationHooks(
    productCostingstate,
    additionalState,
    testingState,
    detailedCostingState,
    detailedQuanitiesState,
    colSizeLengthToggleState?.colourWayToggle,
    colSizeLengthToggleState?.sizeLengthToggle,
    additionalComponentState,
    internalState?.hideColourCol && colSizeLengthToggleState?.colourWayToggle,
  );

  useEffect(() => {
    const callFn = async () => {
      batch(async () => {
        await Promise.all([
          dispatch(getTestingLibraries(devOrderId)),
          dispatch(fetchSupplier(devOrderId)),
          dispatch(bomProductTesting({ devOrderId }) as never),
          dispatch(fetchProductDetails(devOrderId)),
          dispatch(fetchProductLengths(devOrderId as string) as never),
          dispatch(fetchProductColourways(devOrderId as string) as never),
          dispatch(fetchProductSizes(devOrderId as string) as never),
          dispatch(fetchConfig(devOrderId, isSupplierLoggedIn)),
        ]);
      });
    };
    if (!isEmpty(devOrderId) && !isGlobalUrl) callFn();
  }, [dispatch, devOrderId, isSupplierLoggedIn, isGlobalUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if ((productDetails?.suppliers as unknown[] | undefined)?.length && (productDetails?.supplierFactories as unknown[] | undefined)?.length) {
      dispatch(fetchSuppliersFactories(productDetails?.suppliers));
    }
  }, [dispatch, (productDetails?.suppliers as unknown[] | undefined)?.length]);

  useEffect(() => {
    const callFn = async () => {
      await dispatch(getCostingTemplateListByDevOrder(devOrderId));
    };
    if (!isSupplierLoggedIn && !isGlobalUrl && !pathname?.includes('product-costing-supplier') && devOrderId) callFn();
  }, [dispatch, isSupplierLoggedIn, isGlobalUrl, pathname, devOrderId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const callFn = async () => {
      batch(async () => {
        await dispatch(
          getAllUnitDetails({
            loggedInUserId: userInfo?.userId,
            isActive: true,
          }),
        );
      });
    };
    if (!isEmpty(userInfo?.userId) && !isGlobalUrl) callFn();
  }, [dispatch, userInfo?.userId, isGlobalUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userInfo?.companyCurrency && landedInfoState?.landedInfo) {
      const copyState = cloneDeep(landedInfoState);
      set(copyState, landedInfoFieldPath().LANDED_INFO_SELECTED_CURRENCY, userInfo?.companyCurrency);
      setLandedInfoState((prevState) => ({ ...prevState, ...copyState }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.companyCurrency]);

  useEffect(() => {
    if (Array.isArray(productColours) && productColours.length > 0) {
      /* Cloned rather than aliased. The original assigned the live state object and mutated it in
         place, which on the first render mutated the `useState` initial object itself — harmless
         only because that object was rebuilt every render. Now that the initial objects are stable
         for the lifetime of the mount, aliasing here would leak into them permanently. */
      const copyState: InitialOptionsState = cloneDeep(initialOptionsState);
      const ColourwayArr: ColourwayOption[] = productColours.map((val) => {
        return { ...val, value: val.name as string, label: val.name as string, colourRef: val?.newReferences };
      });
      set(copyState, commonPath().COLOURWAY_OPTIONS, ColourwayArr);

      setInitialOptionValues((state) => ({
        ...state,
        ...copyState,
      }));
    }
  }, [productColours, devOrderId]);

  useEffect(() => {
    if (Array.isArray(ColorLibList) && ColorLibList.length > 0) {
      /* Cloned — see the note on the colourway effect above. */
      const copyState: ProductCostingState = cloneDeep(productCostingstate);
      const colourRef: SelectOption[] = ColorLibList.map((val) => {
        return {
          value: `${val.refName ? val.refName : ''} `,
          label: `${val.refName ? val.refName : ''} `,
          _id: val._id,
        };
      });
      set(copyState, commonPath().COLOURWAY_REF_OPTIONS, colourRef);
      setProductCostingState((state) => ({
        ...state,
        ...copyState,
      }));
    }
  }, [ColorLibList, devOrderId]);

  useEffect(() => {
    const fetchData = async () => {
      if (Array.isArray(productSizes) && productSizes?.length > 0) {
        /* Cloned — see the note on the colourway effect above. */
        const copyState: InitialOptionsState = cloneDeep(initialOptionsState);
        const sizeArr = productSizes?.map((val) => ({
          value: val,
          label: val,
          name: val,
        }));
        set(copyState, commonPath().SIZE_OPtIONS, sizeArr);
        setInitialOptionValues((state) => ({
          ...state,
          ...copyState,
        }));
      }
    };

    fetchData();
  }, [productSizes, devOrderId]);

  /**
   * Seeds the costing's sizes from the dev-order master when creating. The supplier
   * link never fetches that master and always edits a saved costing, so running
   * here would clobber the sizes hydrated from the costing document.
   */
  useEffect(() => {
    if (!productid && !isGlobalUrl) {
      setProductCostingState((state) => ({
        ...state,
        costingSizes: productSizes,
      }));
    }
  }, [productSizes?.length, productid, isGlobalUrl]);

  useEffect(() => {
    setProductCostingState((state) => ({
      ...state,
      isGlobalUrl: isGlobalUrl === null ? false : isGlobalUrl,
    }));
  }, [isGlobalUrl]);

  useEffect(() => {
    /* Cloned — see the note on the colourway effect above. */
    const copyState: InitialOptionsState = cloneDeep(initialOptionsState);

    if (Array.isArray(unitList) && unitList.length > 0) {
      set(copyState, commonPath().UOM_OPTIONS, unitList);
      setInitialOptionValues((state) => ({
        ...state,
        ...copyState,
      }));
    } else if (isGlobalUrl && Array.isArray(productUnitList) && productUnitList.length > 0) {
      set(copyState, commonPath().UOM_OPTIONS, productUnitList);
      setInitialOptionValues((state) => ({
        ...state,
        ...copyState,
      }));
    }
  }, [unitList, productUnitList, isGlobalUrl]);

  /**
   * Loads the Sample Ref options for this costing. Scoped to the costing's own product, so unlike
   * the costing-stage master it replaces there is no page-level list to read — it is fetched per
   * costing. The endpoint is JWT-excluded, so the same call serves the non-integrated supplier link.
   * The `cancelled` guard stops a slow response for one costing overwriting a newer one.
   */
  useEffect(() => {
    let cancelled = false;
    /* On /add no costing exists yet, so there is no costing id to resolve the product from — the
       route's own devOrderId is the only handle, and the endpoint accepts either. Prefer the costing
       id when there is one: it also carries the supplier the non-integrated link is scoped by. */
    const scope = effectiveCostingId ? { productId: effectiveCostingId, supplierId: querySupplierId ?? '' } : effectiveDevOrderId ? { devOrderId: effectiveDevOrderId } : null;
    if (!scope) return undefined;
    const loadSampleRefs = async (): Promise<void> => {
      try {
        /* apiClient's response interceptor returns the envelope BODY, so the option array is
           `response.data` — not `response.data.data`. */
        const response = (await getProductCostingSampleRefs(scope)) as { code?: number; data?: SelectOption[] };
        const rows = (response?.data ?? []) as SelectOption[];
        if (!cancelled) setInitialOptionValues((state) => ({ ...state, sampleRefOptionsArr: rows }));
      } catch {
        if (!cancelled) setInitialOptionValues((state) => ({ ...state, sampleRefOptionsArr: [] }));
      }
    };
    loadSampleRefs();
    return () => {
      cancelled = true;
    };
  }, [effectiveCostingId, effectiveDevOrderId, querySupplierId]);

  /** BOM tabs in creation order, each stamped with its 1-based display sequence. */
  const BOMTabsSequence = useMemo<BomTabWithSequence[]>(() => {
    if (tabs?.length) {
      /* Annotated because @types/lodash is not installed — `orderBy` would otherwise be `any`. */
      const sortedTabs: Array<Record<string, unknown>> = orderBy(tabs, ['createdAt'], ['asc']);
      return sortedTabs?.map((tab, index) => ({
        ...tab,
        sequence: index + 1,
      }));
    }
    return [];
  }, [tabs?.length]);

  useEffect(() => {
    const callFn = async () => {
      let clearAllArray: unknown[] = [];
      if (checkArray(supplierChangeKeysArray)) {
        for (const element of supplierChangeKeysArray) {
          const data = await recursiveSearch(productCostingDetail, element);
          clearAllArray = concat(clearAllArray, data);
        }
      }
      clearAllArray = [...new Set(clearAllArray)];
      if (clearAllArray?.includes(true)) {
        setInternalState((state) => ({
          ...state,
          isChangedBySupllierState: true,
        }));
      } else {
        setInternalState((state) => ({
          ...state,
          isChangedBySupllierState: false,
        }));
      }
    };
    if (productCostingDetail && !isSupplierLoggedIn && productid) callFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productCostingDetail, isSupplierLoggedIn, productid]);

  const [automatedFreight, setAutomatedFreight] = useState<AutomatedFreightDto | null>(null);

  useEffect(() => {
    if (!effectiveDevOrderId) return undefined;
    const loadAutomatedFreight = (): void => {
      (dispatch as ProductCostingDispatch)(fetchAutomatedFreight(effectiveDevOrderId)).then((next) =>
        setAutomatedFreight((previous) => (isEqual(previous, next) ? previous : next)),
      );
    };
    const reloadWhenVisible = (): void => {
      if (document.visibilityState === 'visible') loadAutomatedFreight();
    };
    loadAutomatedFreight();
    window.addEventListener('focus', loadAutomatedFreight);
    document.addEventListener('visibilitychange', reloadWhenVisible);
    return () => {
      window.removeEventListener('focus', loadAutomatedFreight);
      document.removeEventListener('visibilitychange', reloadWhenVisible);
    };
  }, [dispatch, effectiveDevOrderId]);

  const contextValue = useMemo<ProductCostingContextValue>(
    () => ({
      productCostingstate,
      setProductCostingState,
      checkValidation,
      globalError,
      setGlobalErrors,
      initialSetting,
      internalState,
      setInternalState,
      isGlobalUrl,
      grandTotalState,
      setGrandTotalState,
      CoastingDeleteRights,
      CoastingAddEditRights,
      setInitialOptionValues,
      initialOptionsState,
      setDefaultTabs,
      defaultTab,
      setTestingState,
      testingState,
      setAdditionalState,
      additionalState,
      setActivateColSizeLengthToggle,
      colSizeLengthToggleState,
      landedInfoState,
      setLandedInfoState,
      initialLandedInfoState,
      landedInfoStates,
      setLandedInfoStates,
      filteredDistributionCentres,
      setCostingLoader,
      loader,
      initialTestingState,
      initialAdditionalState,
      grandTotalObj,
      detailedCostingState,
      setDetailedCostingState,
      detailedQuanitiesState,
      setDetailedQuanitiesState,
      BOMTabsSequence,
      initialDetailedCostingState,
      setCostingCommentLoader,
      commentLoader,
      isAllExpandedState,
      setExpandAllState,
      initialDetailedCostingQuanitiesState,
      setAdditionalComponentState,
      additionalComponentState,
      setSpecificationRequirementState,
      specificationRequirementState,
      reRenderLandedInfo,
      setReRenderLandedInfo,
      automatedFreight,
    }),
    [
      productCostingstate,
      setProductCostingState,
      checkValidation,
      globalError,
      setGlobalErrors,
      initialSetting,
      internalState,
      setInternalState,
      isGlobalUrl,
      grandTotalState,
      setGrandTotalState,
      CoastingAddEditRights,
      CoastingDeleteRights,
      setInitialOptionValues,
      initialOptionsState,
      setDefaultTabs,
      defaultTab,
      setTestingState,
      testingState,
      setAdditionalState,
      additionalState,
      setActivateColSizeLengthToggle,
      colSizeLengthToggleState,
      landedInfoState,
      setLandedInfoState,
      initialLandedInfoState,
      landedInfoStates,
      setLandedInfoStates,
      filteredDistributionCentres,
      setCostingLoader,
      loader,
      initialTestingState,
      initialAdditionalState,
      grandTotalObj,
      detailedCostingState,
      setDetailedCostingState,
      detailedQuanitiesState,
      setDetailedQuanitiesState,
      BOMTabsSequence,
      initialDetailedCostingState,
      setCostingCommentLoader,
      commentLoader,
      isAllExpandedState,
      setExpandAllState,
      initialDetailedCostingQuanitiesState,
      setAdditionalComponentState,
      additionalComponentState,
      setSpecificationRequirementState,
      specificationRequirementState,
      reRenderLandedInfo,
      setReRenderLandedInfo,
      automatedFreight,
    ],
  );

  return <StateContext.Provider value={contextValue}>{children}</StateContext.Provider>;
}

/** Reads the costing context, throwing when used outside the provider. */
export function useProductCostingState(): ProductCostingContextValue {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useProductCostingState must be used within the NewProductCostingDetailsPage');
  }
  return context;
}

export const NewProductCostingStateProvider = NewProductCostingDetailsPage;
